import {useCallback, useMemo, useState} from 'react';

type Params = {
    rows: Array<number | string>,
    totalCount: number,
    onSetSelection?: (ids: Array<number | string>) => void,
    getEntityIds?: () => Promise<{payload?: {success?: boolean, data?: (number | string)[]}}>,
};

type SelectionState = {
    inverted: boolean,
    ids: Array<number | string>,
};

type Return = {
    pageIds: Array<number | string>,
    selectedCount: number,
    allSelected: boolean,
    selection: SelectionState,
    selectAll: () => void,
    selectPage: () => void,
    clearAll: () => void,
    toggle: (id: number | string, rowIndex: number, checked: boolean, shiftKey: boolean) => void,
    isSelected: (id: number | string) => boolean,
};

export const useUserSelection = ({
    rows,
    totalCount,
    onSetSelection,
    getEntityIds,
}: Params): Return => {
    const [selection, setSelection] = useState<SelectionState>({inverted: false, ids: []});
    const [lastCheckedIndex, setLastCheckedIndex] = useState<number | undefined>(undefined);

    const pageIds = useMemo(() => rows, [rows]);

    const setSelectionState = useCallback(async (nextSelection: SelectionState) => {
        setSelection(nextSelection);

        if (!onSetSelection) {
            return;
        }

        if (nextSelection.inverted) {
            if (getEntityIds) {
                const response = await getEntityIds();
                if (response.payload?.success) {
                    onSetSelection(response.payload.data ?? []);
                }
            }
            return;
        }

        onSetSelection(nextSelection.ids);
    }, [getEntityIds, onSetSelection]);

    const applySelection = useCallback((ids: Array<number | string>, checked: boolean) => {
        setSelection(prev => {
            if (prev.inverted) {
                const deselected = new Set(prev.ids);
                ids.forEach(id => {
                    if (checked) {
                        deselected.delete(id);
                    } else {
                        deselected.add(id);
                    }
                });

                return {inverted: true, ids: Array.from(deselected)};
            }

            const selected = new Set(prev.ids);
            ids.forEach(id => {
                if (checked) {
                    selected.add(id);
                } else {
                    selected.delete(id);
                }
            });
            return {inverted: false, ids: Array.from(selected)};
        });
    }, []);

    const selectAll = useCallback(() => {
        setSelectionState({inverted: true, ids: []});
        setLastCheckedIndex(undefined);
    }, [setSelectionState]);

    const clearAll = useCallback(() => {
        setSelectionState({inverted: false, ids: []});
        setLastCheckedIndex(undefined);
    }, [setSelectionState]);

    const selectPage = useCallback(() => {
        setSelection(prev => {
            const next = prev.inverted ?
                    {inverted: true, ids: prev.ids.filter(id => !rows.includes(id))} :
                    {inverted: false, ids: Array.from(new Set([...prev.ids, ...rows]))};

            setSelectionState(next);
            return next;
        });
        setLastCheckedIndex(undefined);
    }, [rows, setSelectionState]);

    const selectedCount = selection.inverted ?
            Math.max(totalCount - selection.ids.length, 0) :
        selection.ids.length;

    const allSelected = selection.inverted ?
        selection.ids.length === 0 && totalCount > 0 :
        selection.ids.length === totalCount && totalCount > 0;

    const toggle = useCallback((id: number | string, rowIndex: number, checked: boolean, shiftKey: boolean) => {
        if (shiftKey && lastCheckedIndex !== undefined) {
            const start = Math.min(rowIndex, lastCheckedIndex);
            const end = Math.max(rowIndex, lastCheckedIndex);
            const rangeIds = rows.slice(start, end + 1);
            applySelection(rangeIds, checked);
            setLastCheckedIndex(rowIndex);
            return;
        }

        applySelection([id], checked);
        setLastCheckedIndex(rowIndex);
    }, [applySelection, lastCheckedIndex, rows]);

    const isSelected = useCallback((id: number | string) => selection.inverted ?
            !selection.ids.includes(id) :
            selection.ids.includes(id), [selection]);

    return {
        pageIds,
        selectedCount,
        allSelected,
        selection,
        selectAll,
        selectPage,
        clearAll,
        toggle,
        isSelected,
    };
};
